import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { take, combineLatest } from 'rxjs';
import { TechDetail, Tag } from 'src/app/models/models';
import { TechService } from 'src/app/services/tech.service';

export type TechsState = {
  isLoading: Boolean,
  techs: TechDetail[],
  tags: Tag[],
  selectedTagIds: string[],
  excludedGenres: string[],
  excludeEcosystem: boolean,
  selectedEcosystemIds: string[],
  search: string
}

const initialState: TechsState = {
  isLoading: true,
  techs: [],
  tags: [],
  selectedEcosystemIds: [],
  selectedTagIds: [],
  excludedGenres: [],
  excludeEcosystem: false,
  search: ''
};

export const TechsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withHooks({
    onInit: (store, api = inject(TechService)) => {
      combineLatest([
        api.getTechsDetails().pipe(take(1)),
        api.getTagsWithDetail().pipe(take(1))
      ])
        .subscribe(([techs, tags]) => patchState(store, (state) => ({
          ...state,
          techs: techs.data || [],
          isLoading: false, tags: tags.data || [],
        })
        ));
    },
  }),
  withComputed(({ techs, selectedEcosystemIds, selectedTagIds, search, tags, excludeEcosystem, excludedGenres }) => {


    const tagsByGenre = computed(() => {
      return tags().reduce((result, tag) => {
        // doesn't Tag also have `tag?.genreId` ?
        const tagGenreId = tag?.tagGenre?.id || ''
        const resultArray = result[tagGenreId] || []
        if (tag?.id) {
          resultArray.push(tag?.id)
          result[tagGenreId] = resultArray;
        }
        return result;
      }, {} as { [key: string]: Tag['id'][] })
    })

    const filteredTechs = computed(() => {
      let result = techs();

      //Filter By search
      if (search().trim()) {
        result = techs()?.filter(tech =>
          tech.name.toLocaleLowerCase().includes(search()?.toLocaleLowerCase() ?? '')
        );
        if (result.length === 0) return result;
      }

      //Filter By Ecosystem
      if (excludeEcosystem()) {
        if (selectedEcosystemIds().length > 0) {
          result = result.filter(tech =>
            !tech.ecosystems?.some(ecosystem => selectedEcosystemIds().includes(ecosystem.id || ''))
          );
        } else {
          result = result.filter(tech => !tech.ecosystems?.length);
        }
      } else if (selectedEcosystemIds().length > 0) {
        result = result.filter(tech =>
          tech.ecosystems?.some(ecosystem => selectedEcosystemIds().includes(ecosystem.id || ''))
        );
      }
      if (result.length === 0) return result;

      //Filter by Tags
      const genres = Object.entries(tagsByGenre());
      for (const [genreId, genreTagIds] of genres) {
        const selectedGenreTags = genreTagIds.filter(t => selectedTagIds().includes(t || ''));
        if (excludedGenres().includes(genreId)) {
          if (selectedGenreTags.length > 0) {
            result = result.filter(tech =>
              !tech.tags?.some(t => selectedGenreTags.includes(t.id || ''))
            );
          } else {
            result = result.filter(tech =>
              !tech.tags?.some(t => genreTagIds.includes(t.id || ''))
            );
          }
        }
        else if (selectedGenreTags.length > 0) {
          result = result.filter(tech =>
            tech.tags?.some(t => selectedGenreTags.includes(t.id || ''))
          );
        }
      }

      return result.sort((t1, t2) =>
        t1.name.toLocaleLowerCase() < t2.name.toLocaleLowerCase() ? -1 : 1
      );
    });


    return {
      tagsByGenre,
      filteredTechs,
    }
  }),
  withMethods((store, api = inject(TechService)) => ({
    updateSelectedTags(selectedTagIds: string[]): void {
      patchState(store, (state) => ({ ...state, selectedTagIds }))
    },
    updateSelectedEcosystems(selectedEcosystemIds: string[]): void {
      patchState(store, (state) => ({ ...state, selectedEcosystemIds }))
    },
    updateSearch(search: string): void {
      patchState(store, (state) => ({ ...state, search }))
    },
    getFilteredTechs: (showAllTechs?: boolean) => {
      return showAllTechs ? store.filteredTechs() : store.filteredTechs().filter((tech) => !tech.isArchived);
    },
    getPagedTechs: (page: number, pageSize: number) => {
      const startIndex = (page - 1) * pageSize
      const endIndex = (Math.min(store.filteredTechs().length || 0, startIndex + pageSize))

      return store.filteredTechs().slice(startIndex, endIndex) || null
    },
    getNextAndPreviousTechSlug(currentSlug: string) {
      const filteredSlugs = store.filteredTechs().map(t => t.slug)
      const index = filteredSlugs.findIndex(slug => slug === currentSlug)
      const previousSlug = index <= 0 ? null : filteredSlugs[index - 1]
      const nextSlug = index >= (filteredSlugs.length - 1) ? null : filteredSlugs[index + 1]

      return { previousSlug, nextSlug }
    },
    getTechs() {
      patchState(store, (state) => ({
        ...state,
        isLoading: true
      }))
      api.getTechsDetails()
        .pipe(take(1))
        .subscribe(techs =>
          patchState(store, (state) => ({
            ...state,
            techs: techs.data || [],
            isLoading: false
          })))
    },
    getTags() {
      patchState(store, (state) => ({
        ...state,
        isLoading: true
      }))

      api.getTagsWithDetail()
        .pipe(take(1))
        .subscribe(tags =>
          patchState(store, (state) => ({
            ...state,
            tags: tags.data || [],
            isLoading: false
          })))
    },
    toggleEcosystemExclude() {
      patchState(store, (state) => ({ ...state, excludeEcosystem: !state.excludeEcosystem }))
    },
    toggleGenreExclude(genre: string) {
      const currentExcludedGenres = store.excludedGenres();
      const index = currentExcludedGenres.indexOf(genre);

      const newExcludedGenres = index === -1
        ? [...currentExcludedGenres, genre]
        : currentExcludedGenres.filter(g => g !== genre);

      patchState(store, (state) => ({
        ...state,
        excludedGenres: newExcludedGenres
      }))
    },
    resetExclude() {
      patchState(store, (state) => ({
        ...state,
        excludeEcosystem: false,
        excludedGenres: []
      }))
    }
  })),
);
